import { useState } from "react";
import { Icon } from "../components/Icon";
import { Avatar, Badge, Btn, Card, PageHead, Seg, StatTile, Skeleton, InvoiceList } from "../components/kit";
import { GrantModal, PlanModal, ImpersonateModal, SuspendModal, GdprModal, ConfirmModal } from "../components/modals";
import type { GodUser } from "../data/mock";
import { useUsers, useUserMemory, useUserLessons, useUserActivity, useUserPath, useUserThreads, fetchAdminThread, useSessions, relTime, nameFromEmail, initialsFrom, grantCredits, changePlan, suspendUser, gdprDelete, impersonateUser, setUserMode, setLessonState, revokeSession, revokeAllSessions, resetOnboarding, resendInvite, type ThreadView, type ThreadTurn } from "../lib/admin";
import type { SetImp } from "../types";

// 4-level fade ladder → operator-friendly labels (L4 = teach-back).
const FADE_LABEL: Record<string, string> = {
  guided: "L1 · Guided", prompted: "L2 · Prompted", independent: "L3 · Independent", teach: "L4 · Teach-back",
};

/* Read-only inspection of a learner's path + stored conversations (Phase 5). */
function ConversationsTab({ userId }: { userId: string }) {
  const path = useUserPath(userId);
  const data = useUserThreads(userId);
  const threads = data?.threads ?? null;
  return (
    <div className="two-col">
      <Card title="Path" icon="route" count={path ? `${path.tracks.length + path.lessons.length} items` : undefined} pad={false}>
        {path === null ? (
          <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={20} />)}</div>
        ) : path.tracks.length === 0 && path.lessons.length === 0 ? (
          <div className="gt-empty">No path items — nothing added or assigned yet.</div>
        ) : (
          <div className="ls-list">
            {path.tracks.map((t) => (
              <div key={t.id}>
                <div className="ls-row" style={{ background: "var(--g-surface-2)" }}>
                  <Icon name={t.icon || "route"} size={14} />
                  <span className="ls-n" style={{ fontWeight: 600 }}>{t.name}</span>
                  <span className={`lvl-tag ${t.source === "assigned" ? "tools" : "skills"}`}>{t.source}</span>
                  <span className="ls-state">{t.mastered}/{t.total}</span>
                </div>
                {t.lessons.map((l) => (
                  <div className="ls-row" key={l.lessonId} style={{ paddingLeft: 26 }}>
                    <span className={`ls-dot ${l.status}`} />
                    <span className="ls-n">{l.title}</span>
                    <span className="ls-fade">{l.fadeLevel ? FADE_LABEL[l.fadeLevel] ?? l.fadeLevel : "—"}</span>
                    <span className="ls-state">{l.status.replace("_", " ")}</span>
                  </div>
                ))}
              </div>
            ))}
            {path.lessons.map((l) => (
              <div className="ls-row" key={l.lessonId}>
                <span className={`ls-dot ${l.status}`} />
                <span className="ls-n">{l.title}{l.source === "assigned" && <span className="lvl-tag tools" style={{ marginLeft: 8 }}>assigned</span>}</span>
                <span className="ls-fade">{l.fadeLevel ? FADE_LABEL[l.fadeLevel] ?? l.fadeLevel : "—"}</span>
                <span className="ls-state">{l.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card title="Conversations" icon="message-square" count={threads ? threads.length : undefined} pad={false}>
        {threads === null ? (
          <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={20} />)}</div>
        ) : threads.length === 0 ? (
          <div className="gt-empty">No stored conversations (the learner may have history retention off).</div>
        ) : (
          <div>{threads.map((t) => <ThreadRow key={t.id} userId={userId} t={t} />)}</div>
        )}
      </Card>
    </div>
  );
}

function ThreadRow({ userId, t }: { userId: string; t: ThreadView }) {
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<ThreadTurn[] | null>(null);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && turns === null) fetchAdminThread(userId, t.id).then((d) => setTurns(d.turns)).catch(() => setTurns([]));
  };
  return (
    <div style={{ borderTop: "1px solid var(--g-line)" }}>
      <button onClick={toggle} style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "11px 12px", background: "none", border: "none", cursor: "pointer", textAlign: "left", color: "inherit" }}>
        <Icon name={open ? "chevron-down" : "chevron-right"} size={14} />
        <Icon name={t.mode === "coach" ? "graduation-cap" : "message-square"} size={14} />
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>{t.title}</span>
        {t.lessonId && <span className="lvl-tag skills">lesson</span>}
        <span className="muted-sm">{t.turns} turns · {relTime(t.lastMessageAt)} · {t.status}</span>
      </button>
      {open && (
        <div style={{ padding: "0 12px 14px 34px", display: "flex", flexDirection: "column", gap: 12 }}>
          {turns === null ? <Skeleton h={48} /> : turns.length === 0 ? <div className="muted-sm">No turns recorded.</div> : turns.map((turn, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 12.5, lineHeight: 1.5 }}><span className="lvl-tag tools" style={{ marginRight: 8 }}>User</span>{turn.user || "—"}</div>
              <div style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--g-fg-2)" }}><span className="lvl-tag mindset" style={{ marginRight: 8 }}>Orbit</span>{turn.assistant || "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function UsersPage({ setImp }: { setImp: SetImp }) {
  const [sel, setSel] = useState<GodUser | null>(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [plan, setPlan] = useState("All");
  const [checked, setChecked] = useState<string[]>([]);
  const [bulk, setBulk] = useState<string | null>(null);

  // Live cross-org roster; null until loaded / signed out.
  const live = useUsers();
  const allUsers: GodUser[] | null = live
    ? live.users.map((u) => {
        const name = nameFromEmail(u.email);
        return {
          id: u.id, orgId: u.orgId, name, email: u.email, org: u.org, role: u.role, plan: u.plan,
          status: "active", last: relTime(u.lastTs), turns: u.turns,
          cost: u.costUsd, margin: u.marginPct ?? 0, credits: "—", mastery: u.mastery, initials: initialsFrom(name),
        };
      })
    : null;

  if (sel) return <UserDetail u={sel} back={() => setSel(null)} setImp={setImp} />;

  const rows = (allUsers ?? []).filter(
    (u) =>
      (status === "All" || (status === "Active" && u.status === "active") || (status === "Suspended" && u.status === "suspended")) &&
      (plan === "All" || u.plan === plan) &&
      (q === "" || (u.name + u.email + u.org).toLowerCase().includes(q.toLowerCase()))
  );
  const toggle = (id: string) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const allChecked = rows.length > 0 && rows.every((r) => checked.includes(r.id));

  return (
    <div className="page">
      <PageHead title="Users" sub="Every user across every org. Filter, drill in, override." />
      <div className="filter-bar">
        <div className="search-inline g grow"><Icon name="search" size={15} /><input placeholder="Search name, email, org…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <div className="seg g">{["All", "Active", "Suspended"].map((s) => <button key={s} aria-pressed={status === s} onClick={() => setStatus(s)}>{s}</button>)}</div>
        <select className="gselect" value={plan} onChange={(e) => setPlan(e.target.value)}>{["All", "Free", "Pro", "Team", "Founder"].map((p) => <option key={p}>{p}</option>)}</select>
        <Btn variant="ghost" size="sm" icon="download">Export CSV</Btn>
      </div>

      <Card pad={false}>
        {checked.length > 0 && (
          <div className="bulk-bar">
            <span className="bulk-n"><Icon name="check-square" size={14} />{checked.length} selected</span>
            <div className="bulk-actions">
              <Btn variant="ghost" size="sm" icon="zap" onClick={() => setBulk("grant")}>Grant credits</Btn>
              <Btn variant="ghost" size="sm" icon="arrow-left-right" onClick={() => setBulk("plan")}>Change plan</Btn>
              <Btn variant="ghost" size="sm" icon="mail">Message</Btn>
              <Btn variant="danger" size="sm" icon="pause" onClick={() => setBulk("suspend")}>Suspend</Btn>
            </div>
            <button className="bulk-clear" onClick={() => setChecked([])}>Clear</button>
          </div>
        )}
        <div className="gtable users-t">
          <div className="gt-head">
            <span className="gt-check"><input type="checkbox" checked={allChecked} onChange={() => setChecked(allChecked ? [] : rows.map((r) => r.id))} /></span>
            <span>User</span><span>Org</span><span>Plan</span><span>Turns</span><span>Cost</span><span>Margin</span><span>Last</span><span></span>
          </div>
          {allUsers === null
            ? Array.from({ length: 8 }).map((_, i) => (
                <div className="gt-row" key={i} style={{ alignItems: "center" }}>
                  <span className="gt-check" /><span className="gt-user"><Skeleton w={28} h={28} r={14} /><Skeleton w={120} h={12} /></span>
                  <span><Skeleton w={70} /></span><span><Skeleton w={44} /></span><span><Skeleton w={36} /></span>
                  <span><Skeleton w={44} /></span><span><Skeleton w={36} /></span><span><Skeleton w={40} /></span><span />
                </div>
              ))
            : rows.map((u) => (
                <div className={`gt-row click ${checked.includes(u.id) ? "checked" : ""}`} key={u.id} onClick={() => setSel(u)}>
                  <span className="gt-check" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={checked.includes(u.id)} onChange={() => toggle(u.id)} /></span>
                  <span className="gt-user"><Avatar initials={u.initials} size={28} /><span><b>{u.name}</b><small>{u.email}</small></span></span>
                  <span className="gt-mut">{u.org}</span>
                  <span><span className={`plan-pill ${u.plan.toLowerCase()}`}>{u.plan}</span></span>
                  <span className="gt-num">{u.turns.toLocaleString()}</span>
                  <span className="gt-num">${u.cost.toFixed(2)}</span>
                  <span className={`gt-num ${u.margin < 40 ? "bad" : u.margin > 60 ? "good" : ""}`}>{u.margin}%</span>
                  <span className="gt-mut">{u.status === "suspended" ? <Badge tone="danger">Suspended</Badge> : u.last}</span>
                  <span className="gt-go"><Icon name="chevron-right" size={16} /></span>
                </div>
              ))}
          {allUsers !== null && rows.length === 0 && <div className="gt-empty">No users match these filters.</div>}
        </div>
      </Card>

      {bulk === "grant" && <GrantModal target={`${checked.length} selected users`} onClose={() => setBulk(null)} />}
      {bulk === "plan" && <PlanModal target={`${checked.length} selected users`} current="Pro" onClose={() => setBulk(null)} />}
      {bulk === "suspend" && <SuspendModal target={`${checked.length} selected users`} onClose={() => setBulk(null)} />}
    </div>
  );
}

const U_TABS: [string, string, string][] = [
  ["overview", "Overview", "layout-dashboard"], ["learning", "Learning", "graduation-cap"], ["conversations", "Conversations", "message-square"],
  ["memory", "Memory", "brain-circuit"], ["sessions", "Sessions", "monitor-smartphone"], ["activity", "Activity", "history"], ["billing", "Billing", "credit-card"],
];

function UserDetail({ u, back, setImp }: { u: GodUser; back: () => void; setImp: SetImp }) {
  const [tab, setTab] = useState("overview");
  const [modal, setModal] = useState<string | null>(null);
  const open = (m: string) => setModal(m);
  const memoryRows = useUserMemory(u.id); // live per-user memories; null until loaded
  const lessons = useUserLessons(u.id);
  const activity = useUserActivity(u.id);
  const { sessions, reload: reloadSessions } = useSessions(u.id);
  const [mode, setMode] = useState("Open");
  const changeMode = (m: string) => {
    setMode(m);
    setUserMode(u.id, m === "Assigned path" ? "assigned" : "open").catch(() => {});
  };
  // Optimistic overlay of operator lesson-status overrides (mark mastered / reset),
  // so the row updates immediately without refetching the whole catalog.
  const [lessonOverride, setLessonOverride] = useState<Record<string, string>>({});
  const overrideLesson = (lessonId: string, state: "mastered" | "reset") => {
    setLessonOverride((m) => ({ ...m, [lessonId]: state === "mastered" ? "retired" : "available" }));
    setLessonState(u.id, lessonId, state).catch(() => setLessonOverride((m) => {
      const next = { ...m }; delete next[lessonId]; return next;
    }));
  };
  return (
    <div className="page">
      <div className="detail-head">
        <button className="back-btn" onClick={back}><Icon name="arrow-left" size={16} /></button>
        <Avatar initials={u.initials} size={46} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="dh-name">{u.name} {u.status === "suspended" && <Badge tone="danger">Suspended</Badge>} <span className={`plan-pill ${u.plan.toLowerCase()}`}>{u.plan}</span></div>
          <div className="dh-sub">{u.email} · {u.org} · {u.role} · id {u.id}</div>
        </div>
        <div className="dh-actions">
          <Btn variant="ghost" size="sm" icon="eye">View-as</Btn>
          <Btn variant="danger" size="sm" icon="venetian-mask" onClick={() => open("imp")}>Impersonate</Btn>
        </div>
      </div>

      <div className="stat-row">
        <StatTile icon="zap" value={u.credits} label="Credits" tone="live" />
        <StatTile icon="activity" value={u.turns.toLocaleString()} label="Turns" />
        <StatTile icon="dollar-sign" value={`$${u.cost.toFixed(2)}`} label="Cost (cycle)" />
        <StatTile icon="percent" value={`${u.margin}%`} label="Margin" />
      </div>

      <div className="tabbar">
        {U_TABS.map(([id, label, ic]) => <button key={id} className={`tab ${tab === id ? "on" : ""}`} onClick={() => setTab(id)}><Icon name={ic} size={15} />{label}</button>)}
      </div>

      {tab === "overview" && (
        <div className="two-col">
          <div className="stack-lg">
            <Card title="Account facts" icon="id-card">
              <div className="kv"><span>Org</span><b>{u.org}</b></div>
              <div className="kv"><span>Role</span><b>{u.role}</b></div>
              <div className="kv"><span>Plan</span><span className={`plan-pill ${u.plan.toLowerCase()}`}>{u.plan}</span></div>
              <div className="kv"><span>Credit balance</span><b>{u.credits}</b></div>
              <div className="kv"><span>Completion</span><b>{u.mastery}</b></div>
              <div className="kv"><span>Last active</span><b>{u.last}</b></div>
            </Card>
            <Card title="Quick actions" icon="zap">
              <div className="god-actions">
                <Btn variant="ghost" size="sm" icon="plus" onClick={() => open("grant")}>Grant credits</Btn>
                <Btn variant="ghost" size="sm" icon="arrow-left-right" onClick={() => open("plan")}>Change plan</Btn>
                <Btn variant="ghost" size="sm" icon="crown" onClick={() => open("comp")}>Comp a plan</Btn>
                <Btn variant="ghost" size="sm" icon="rotate-ccw" onClick={() => open("reset")}>Reset onboarding</Btn>
              </div>
            </Card>
          </div>
          <Card title="Danger zone" icon="shield-alert" iconColor="var(--g-danger)">
            <div className="danger-list">
              <button className="danger-row" onClick={() => open("revoke")}><Icon name="key-round" size={15} />Revoke sessions / force re-auth</button>
              <button className="danger-row" onClick={() => open("invite")}><Icon name="mail" size={15} />Resend WorkOS invite</button>
              <button className="danger-row" onClick={() => open("suspend")}><Icon name="pause" size={15} />Suspend user</button>
              <button className="danger-row crit" onClick={() => open("gdpr")}><Icon name="trash-2" size={15} />Delete all data (GDPR)</button>
            </div>
          </Card>
        </div>
      )}

      {tab === "learning" && (
        <Card title="Learning state" icon="graduation-cap" count={u.mastery + " complete"}
          action={<div style={{ display: "flex", gap: 8 }}><Btn variant="ghost" size="sm" icon="rotate-ccw" onClick={() => open("reset")}>Reset onboarding</Btn><Btn variant="ghost" size="sm" icon="fast-forward">Advance</Btn></div>} pad={false}>
          <div className="set-row" style={{ borderBottom: "1px solid var(--g-line)" }}>
            <span className="set-ic"><Icon name="route" size={17} /></span>
            <div className="set-body"><div className="set-t">Curriculum mode</div><div className="set-d">Open = explore freely · Assigned path = locked to assigned tracks</div></div>
            <Seg options={["Open", "Assigned path"]} value={mode} onChange={changeMode} />
          </div>
          {lessons === null ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} h={20} />)}</div>
          ) : lessons.items.length === 0 ? (
            <div className="gt-empty">No lessons in the catalog.</div>
          ) : (
            <div className="ls-list">
              {lessons.items.map((l) => {
                const status = lessonOverride[l.lessonId] ?? l.status;
                const isMastered = status === "retired";
                return (
                  <div className="ls-row" key={l.lessonId}>
                    <span className={`ls-dot ${status}`} />
                    <span className="ls-n">{l.title}</span>
                    <span className={`lvl-tag ${l.type}`}>{l.type}</span>
                    <span className="ls-fade">{isMastered ? "Complete" : l.fadeLevel ? FADE_LABEL[l.fadeLevel] ?? l.fadeLevel : "—"}</span>
                    <span className="ls-state">{status.replace("_", " ")}</span>
                    {isMastered
                      ? <Btn variant="ghost" size="sm" icon="rotate-ccw" onClick={() => overrideLesson(l.lessonId, "reset")}>Reset</Btn>
                      : <Btn variant="live" size="sm" icon="check-check" onClick={() => overrideLesson(l.lessonId, "mastered")}>Mark complete</Btn>}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {tab === "conversations" && <ConversationsTab userId={u.id} />}

      {tab === "memory" && (
        <Card title="Memory (user_memory)" icon="brain-circuit" iconColor="var(--g-violet)" count={memoryRows ? memoryRows.length : undefined} pad={false}>
          {memoryRows === null ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} h={18} />)}</div>
          ) : memoryRows.length === 0 ? (
            <div className="gt-empty">No memories stored for this user yet.</div>
          ) : (
            <div className="mem-rows">
              {memoryRows.map((m, i) => (
                <div className="mem-row" key={i}>
                  <span className="mem-kind">{m.kind}</span><span className="mem-txt">{m.text}</span><span className="mem-conf">{Math.round(m.conf * 100)}%</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "sessions" && (
        <Card title="Active sessions & devices" icon="monitor-smartphone" count={sessions ? `${sessions.filter((s) => !s.revoked).length} active` : undefined}
          action={<Btn variant="danger" size="sm" icon="key-round" onClick={() => open("revoke")}>Revoke all</Btn>} pad={false}>
          {sessions === null ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} h={28} />)}</div>
          ) : sessions.length === 0 ? (
            <div className="gt-empty">No sessions recorded yet (the user hasn't signed in since session tracking went live).</div>
          ) : (
            <div className="sess-list">
              {sessions.map((s) => (
                <div className={`sess ${s.revoked ? "revoked" : ""}`} key={s.id}>
                  <span className="sess-ic"><Icon name={s.device.includes("iPhone") || s.device.includes("Android") ? "smartphone" : s.device.includes("Web") ? "globe" : "laptop"} size={17} /></span>
                  <div className="sess-body">
                    <div className="sess-t">{s.device}{s.revoked && <span className="sess-cur" style={{ color: "var(--g-danger)" }}>revoked</span>}</div>
                    <div className="sess-m">{[s.ip, `seen ${relTime(s.lastSeen)}`].filter(Boolean).join(" · ")}</div>
                  </div>
                  {!s.revoked && <button className="icon-mini" title="Revoke this session" onClick={() => revokeSession(s.id).then(reloadSessions).catch(() => {})}><Icon name="x" size={15} /></button>}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "activity" && (
        <Card title="Recent activity" icon="history" count={activity ? `${activity.events.length}` : undefined} pad={false}>
          {activity === null ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} h={20} />)}</div>
          ) : activity.events.length === 0 ? (
            <div className="gt-empty">No recorded activity for this user yet.</div>
          ) : (
            <div className="uact-list">
              {activity.events.map((a, i) => (
                <div className={`uact ${a.kind === "override" ? "flag" : ""}`} key={i}>
                  <span className="uact-ic"><Icon name={a.icon} size={15} /></span>
                  <div className="uact-body"><div className="uact-t">{a.title}</div><div className="uact-d">{a.detail}</div></div>
                  <span className="uact-when">{relTime(a.ts)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "billing" && (
        <div className="two-col">
          <Card title="Billing & entitlements" icon="credit-card">
            <div className="kv"><span>Plan</span><span className={`plan-pill ${u.plan.toLowerCase()}`}>{u.plan}</span></div>
            <div className="kv"><span>Credit balance</span><b>{u.credits}</b></div>
            <div className="kv"><span>Cost this cycle</span><b>${u.cost.toFixed(2)}</b></div>
            <div className="kv"><span>Margin</span><b>{u.margin}%</b></div>
            <div className="god-actions">
              <Btn variant="ghost" size="sm" icon="plus" onClick={() => open("grant")}>Grant credits</Btn>
              <Btn variant="ghost" size="sm" icon="arrow-left-right" onClick={() => open("plan")}>Change plan</Btn>
              <Btn variant="ghost" size="sm" icon="crown" onClick={() => open("comp")}>Comp a plan</Btn>
            </div>
          </Card>
          <Card title="Recent charges" icon="receipt" pad={false}>
            <InvoiceList query={{ user: u.id }} />
          </Card>
        </div>
      )}

      {modal === "imp" && <ImpersonateModal target={u.email} onClose={() => setModal(null)} onConfirm={() => { setModal(null); impersonateUser(u.id, "operator debug session", u.email).catch(() => {}); setImp({ name: u.name, email: u.email, initials: u.initials }); }} />}
      {modal === "grant" && <GrantModal target={u.email} onClose={() => setModal(null)} onConfirm={(amount, reason) => { if (u.orgId) grantCredits(u.orgId, amount, reason, u.email).catch(() => {}); }} />}
      {modal === "plan" && <PlanModal target={u.email} current={u.plan} onClose={() => setModal(null)} submit={({ plan, reason, mode, netTermsDays }) => u.orgId ? changePlan(u.orgId, plan, reason, u.email, mode, netTermsDays) : Promise.resolve({ ok: false })} />}
      {modal === "comp" && <PlanModal target={u.email} current={u.plan} preset="Team" presetMode="comp" onClose={() => setModal(null)} submit={({ plan, reason, mode, netTermsDays }) => u.orgId ? changePlan(u.orgId, plan, reason, u.email, mode, netTermsDays) : Promise.resolve({ ok: false })} />}
      {modal === "suspend" && <SuspendModal target={u.email} onClose={() => setModal(null)} onConfirm={(reason) => suspendUser(u.id, reason, u.email).catch(() => {})} />}
      {modal === "gdpr" && <GdprModal target={u.email} onClose={() => setModal(null)} onConfirm={() => gdprDelete(u.id, "GDPR request", u.email).catch(() => {})} />}
      {modal === "revoke" && <ConfirmModal title="Revoke all sessions" icon="key-round" tone="danger" desc={`${u.email} will be signed out of every device and must re-authenticate.`} confirmLabel="Revoke & force re-auth" onClose={() => setModal(null)} onConfirm={() => revokeAllSessions(u.id, "operator force re-auth").then(reloadSessions).catch(() => {})} />}
      {modal === "invite" && <ConfirmModal title="Resend WorkOS invite" icon="mail" desc={`Send a fresh sign-in link to ${u.email}.`} confirmLabel="Resend invite" onClose={() => setModal(null)} onConfirm={() => resendInvite(u.id).catch(() => {})} />}
      {modal === "reset" && <ConfirmModal title="Reset onboarding" icon="rotate-ccw" tone="danger" desc={`Clears the interview and lesson state for ${u.email}. They’ll re-run first-run on next launch.`} confirmLabel="Reset onboarding" onClose={() => setModal(null)} onConfirm={() => resetOnboarding(u.id, "operator reset").catch(() => {})} />}
    </div>
  );
}
